"use client";

import React, { useState, useEffect } from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const ParticleBackground = () => {
  const [particles, setParticles] = useState<Array<{
    id: number;
    delay: number;
    duration: number;
    x: string;
    size: number;
  }>>([]);
  useEffect(() => {
    const particleCount = Math.min(Math.floor(window.innerWidth / 10), 100);
    const newParticles = Array.from({
      length: particleCount
    }, (_, i) => ({
      id: i,
      delay: Math.random() * 5,
      duration: 10 + Math.random() * 10,
      x: `${Math.random() * 100}%`,
      size: 1 + Math.random() * 2
    }));
    setParticles(newParticles);
  }, []);
  return <div className="absolute inset-0 z-0 opacity-40 pointer-events-none overflow-hidden">
      {particles.map(particle => <motion.div key={particle.id} className="absolute rounded-full bg-white" style={{
      left: particle.x,
      width: `${particle.size}px`,
      height: `${particle.size}px`
    }} initial={{
      y: "100vh",
      opacity: 0.2
    }} animate={{
      y: "-10vh",
      opacity: [0.2, 0.6, 0.2]
    }} transition={{
      duration: particle.duration,
      delay: particle.delay,
      repeat: Infinity,
      ease: "linear"
    }} />)}
    </div>;
};
function WaitlistForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-medium text-foreground">
          You're on the list
        </h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          {successMessage || "Thank you for joining the priority access waitlist."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 hover:text-primary">
          Register another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <input type="hidden" name="email_to" value="keertivesh@paawanair.com" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Aarav Sharma" {...field} className="bg-background/50 border-white/10 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="aarav@example.com" {...field} className="bg-background/50 border-white/10 transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="text-muted-foreground">Phone Number <span className="text-xs opacity-50">(Optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+91-9876543210" {...field} className="bg-background/50 border-white/10 transition-all" />
              </FormControl>
              <FormDescription className="text-xs">We may use this for SMS updates.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="organization_type" rules={{
        required: "Please select an option"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">I'm signing up for</FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-md border border-white/10 bg-background/50 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 text-foreground" {...field}>
                  <option value="" disabled>Select an option</option>
                  {["Home", "School", "Office", "Other"].map((opt, i) => <option key={i} value={opt} className="bg-card text-foreground">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="city" rules={{
        required: "Please select your city"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground">City (Delhi-NCR)</FormLabel>
              <FormControl>
                <select className="flex h-9 w-full rounded-md border border-white/10 bg-background/50 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 text-foreground" {...field}>
                  <option value="" disabled>Select your city</option>
                  {["Delhi", "Gurgaon", "Noida", "Faridabad", "Other"].map((opt, i) => <option key={i} value={opt} className="bg-card text-foreground">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2 text-destructive text-sm">
          <CircleAlert className="h-4 w-4" />
          <span>{submitError}</span>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)] transition-all duration-300 h-11 mt-4">Join Wait-list<br /><br /></Button>
    </Form>;
}
export default function PriorityAccessWaitlist() {
  return <section id="priority-access-waitlist" className="dark bg-background text-foreground relative w-full min-h-[800px] flex items-center justify-center py-24 px-4 overflow-hidden">
      {}
      <ParticleBackground />

      {}
      <div className="relative z-10 w-full max-w-xl mx-auto">
        <Card className="bg-card/40 backdrop-blur-xl shadow-[0_0_40px_-10px_var(--color-primary)] overflow-hidden">
          {}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70" />
          
          <CardHeader className="text-center space-y-4 pb-2">
            <div className="mx-auto mb-2">
              <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Limited Availability
              </span>
            </div>
            <CardTitle className="text-3xl md:text-4xl font-serif tracking-tight text-foreground">
              Priority Access Waitlist
            </CardTitle>
            <CardDescription className="text-base md:text-lg text-muted-foreground font-light">
              The first 500 installations in Delhi-NCR will receive priority calibration.
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <FormProvider formId="priority_waitlist_home" sectionName="priority-access-waitlist" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            organization_type: "",
            city: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            organization_type: {
              required: true
            },
            city: {
              required: true
            }
          }} submitText="Request Access" successMessage="You have been added to our priority waitlist. We will contact you shortly.">
              <WaitlistForm />
            </FormProvider>
          </CardContent>
          
          <CardFooter className="justify-center border-t border-white/5 pt-6 pb-6">
            <p className="text-xs text-muted-foreground/60 text-center">
              By joining, you agree to receive updates about PāAWAN's launch. 
              <br />We respect your privacy and will never spam.
            </p>
          </CardFooter>
        </Card>
      </div>
    </section>;
}